import type { TerningTall } from '@/data/kritikere';
import type { Manuscript } from '@/data/types';
import { sesongForDag, sesongSalgFaktor, SESONG_NAVN } from '@/systems/sesong';

/**
 * «Les et utdrag»-laget (spilltest-funn: manusvalget var blindt).
 * Å lese koster 60 min (data/timeCosts.ts) og gir redaktørens
 * magefølelse: en terning, et lesernotat og en sesong-linje.
 *
 * Terningen er deterministisk per ISBN og vektet av tier — tier driver
 * faktisk salg, så magefølelsen er et ærlig (men upresist) signal.
 */

export interface ManusInntrykkResultat {
  terning: TerningTall;
  notat: string;
  sesongLinje: string;
}

/** Stabil hash av en streng — samme ISBN gir alltid samme inntrykk. */
function hashAv(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

const TERNING_SPENN: Record<string, [number, number]> = {
  D: [2, 4],
  C: [3, 5],
  B: [3, 6],
  A: [4, 6],
  S: [4, 6],
};

export function terningFor(manus: Manuscript): TerningTall {
  const [lav, hoy] = TERNING_SPENN[manus.tier] ?? [2, 4];
  // Spenn-tabellen holder seg innenfor 2..6, så resultatet er alltid gyldig.
  return (lav + (hashAv(manus.isbn) % (hoy - lav + 1))) as TerningTall;
}

const NOTATER_HOY: string[] = [
  'Sterk åpning — du glemte kaffen din på side tre.',
  'Stemmen sitter fra første avsnitt. Dette kan bli noe.',
  'Du noterer i margen: «JA». To ganger.',
  'Karakterene puster selv. Slutten må strammes, men kjernen er ekte.',
];

const NOTATER_MIDT: string[] = [
  'Solid håndverk, men du har lest denne boka før — av andre.',
  'Sterk midte, slapp åpning. Kan reddes med en streng redaktør.',
  'Det er noe her. Du er bare ikke sikker på om det er nok.',
  'Kompetent. Ordet du hater mest i en vurdering.',
];

const NOTATER_LAV: string[] = [
  'Tre adjektiver per substantiv. Du teller dem etter hvert.',
  'Åpner med en drøm. Du lukker med et sukk.',
  'Forfatteren har talent — for noe annet, mistenker du.',
  'Side én lover mye. Side to holder ikke ord.',
];

export function manusInntrykk(manus: Manuscript, dag: number): ManusInntrykkResultat {
  const terning = terningFor(manus);
  const pool = terning >= 5 ? NOTATER_HOY : terning >= 3 ? NOTATER_MIDT : NOTATER_LAV;
  const notat = pool[hashAv(`${manus.isbn}-notat`) % pool.length];

  const sesong = sesongForDag(dag);
  const faktor = sesongSalgFaktor(dag, manus.genre);
  const sesongLinje =
    faktor >= 1.15
      ? `${manus.genre} selger godt i ${SESONG_NAVN[sesong].toLowerCase()} akkurat nå.`
      : faktor <= 0.85
        ? `${manus.genre} er tungt å selge i ${SESONG_NAVN[sesong].toLowerCase()}.`
        : `${manus.genre} går som normalt i ${SESONG_NAVN[sesong].toLowerCase()}.`;

  return { terning, notat, sesongLinje };
}

/** Flagg-navn for «utdraget er lest» — lagres i GameState.flags. */
export function lestFlaggFor(isbn: string): string {
  return `manus_lest_${isbn}`;
}
